import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { router } from './router'
import { hydrateCache } from './lib/hydrate'
import { initErrorTracking } from './lib/errorTracking'
import { apiGet } from './lib/api'
import { isStaleChunkError, handleStaleChunk, clearStaleChunkFlag } from './lib/staleChunk'
import './fonts.css'
import './index.css'

// Stale code-split chunk recovery (see src/lib/staleChunk.ts). Covers three
// surfaces: uncaught window errors, unhandled promise rejections, and Vite's
// native dynamic-import-failure event. React-caught lazy-import rejections take
// a different path and are handled in ErrorBoundary, which shares the same
// helper so a single sessionStorage guard governs all of them.
window.addEventListener('error', (event) => {
  if (isStaleChunkError(event.message)) handleStaleChunk()
})
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  const message = reason instanceof Error ? reason.message : reason
  if (isStaleChunkError(message)) handleStaleChunk()
})
// Vite dispatches this when a dynamically imported module fails to load — the
// canonical, proactive signal for a stale chunk after deploy.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  handleStaleChunk()
})
// Once the app successfully boots, clear the flag so a future stale chunk
// can trigger another reload.
window.addEventListener('load', () => {
  // Defer past initial render so a broken first render still consumes the flag.
  setTimeout(clearStaleChunkFlag, 4000)
})

// Initialize GlitchTip/Sentry error tracking AFTER window 'load' so the
// @sentry/browser chunk (dynamic import inside initErrorTracking — see
// src/lib/errorTracking.ts) never competes with the critical bundle. The DSN
// lives in the `error_tracking_config` settings blob (runtime config). Repeat
// visitors init from the localStorage cache; first visits init once
// /api/config resolves. initErrorTracking is idempotent. Routing the config
// fetch through apiGet (not raw fetch) shares the in-flight `GET /api/config`
// cache key with hydrateCache()/siteData, keeping it to one request.
function startErrorTracking() {
  try {
    const cached = localStorage.getItem('ck_cache_config_error_tracking_config')
    if (cached) void initErrorTracking(JSON.parse(cached))
  } catch {
    /* ignore malformed cache */
  }
  apiGet<{ error_tracking_config?: Parameters<typeof initErrorTracking>[0] }>('/api/config')
    .then((map) => {
      if (map && typeof map === 'object') void initErrorTracking(map.error_tracking_config)
    })
    .catch(() => {
      /* offline / non-fatal */
    })
}
if (document.readyState === 'complete') {
  startErrorTracking()
} else {
  window.addEventListener('load', startErrorTracking, { once: true })
}

// Fire-and-forget: warm the localStorage cache before the React tree mounts.
// useResource() in siteData.ts reads `ck_cache_*` synchronously on mount, so
// any slice that lands before mount eliminates the fallback-content flash.
// Awaiting would block initial paint — explicitly do not await.
void hydrateCache()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
)
